/**
 * Copyright 2022-2025 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { createColumnHelper } from "@tanstack/react-table";
import dayjs from "dayjs";
import styled from "styled-components";
import { FlexContainer, Text, theme, Tag } from "@nordcloud/gnui";
import { ChangeDetails, ActionType } from "~/generated/graphql";
import { NameWithLink } from "~/components/Tables/cells/NameWithLink";
import {
  MediumIconColumn,
  FlexColumn,
  SmallColumn,
} from "~/components/Tables/styles";
import { dateFormat, TABLE_LABELS } from "~/constants";
import { ROUTES } from "~/routing/routes";
import { ACTION_TYPE_LABEL_MAP } from "~/views/customActions/constants";
import { Action } from "./Action";
import { getScope } from "./types";

export type CustomActions = {
  id: string;
  name: string;
  color: string;
  scope: string;
  description: string;
  type: string;
  changeDetails: ChangeDetails;
};

const columnHelper = createColumnHelper<CustomActions>();

export function columns() {
  return [
    columnHelper.accessor(() => null, {
      id: "color",
      header: () => null,
      cell: ({ row }) => {
        return (
          <FlexContainer justifyContent="center" alignItems="center">
            <ColorBox
              css={{ alignItems: "center" }}
              color={row.original?.color}
            />
          </FlexContainer>
        );
      },
      meta: MediumIconColumn,
    }),
    columnHelper.accessor((row) => row?.name, {
      header: "Action Name",
      cell: ({ row }) => (
        <NameWithLink
          name={row.original.name}
          link={ROUTES.customActions.generateDetails(row.original.id)}
        />
      ),
      meta: FlexColumn,
    }),
    columnHelper.accessor((row) => row.scope, {
      id: "scope",
      header: TABLE_LABELS.scope,
      cell: ({ row }) => {
        return <Tag text={getScope(row.original.scope)} />;
      },
      meta: SmallColumn,
    }),
    columnHelper.accessor((row) => row.type, {
      id: "type",
      header: TABLE_LABELS.type,
      cell: ({ row }) => {
        const label =
          ACTION_TYPE_LABEL_MAP[row.original.type as unknown as ActionType] ??
          row.original.type;
        return <Tag text={label} />;
      },
      meta: SmallColumn,
    }),
    columnHelper.accessor((row) => row.description, {
      id: "description",
      header: "Description",
      cell: ({ row }) => {
        return <Text>{row.original.description}</Text>;
      },
      meta: FlexColumn,
    }),
    columnHelper.accessor((row) => row.changeDetails?.createdAt ?? "", {
      id: "createdAt",
      header: TABLE_LABELS.createdAt,
      cell: (cell) => dayjs(cell.getValue()).format(dateFormat.dayMonthYear),
      meta: FlexColumn,
    }),
    columnHelper.accessor(() => null, {
      id: "options",
      header: () => null,
      cell: ({ row }) => {
        const actionId = row.original?.id;

        return <Action {...{ actionId }} />;
      },
      meta: MediumIconColumn,
    }),
  ] as never;
}

const ColorBox = styled.div`
  background: ${(props) => (props.color ? props.color : theme.colors.darks[4])};
  width: ${theme.spacing.spacing06};
  height: ${theme.spacing.spacing06};
  border-radius: ${theme.spacing.spacing01};
`;
